import * as React from 'react'
import { THEME_COLORS } from '@getgreenline/shared'
import { UBER_EATS_TIME_PILL_STYLES } from '../../constants/UberEats'
import { IUberEatsIncomingOrder, UberEatsUrgencyStage } from '../../models/UberEats/UberEatsModels'
import { formatAlertBarHeadline } from '../../utilities/uberEatsIncomingOrder'
import { UberEatsActionButton } from './UberEatsActionButton'
import { UberEatsStageBadge } from './UberEatsStageBadge'

interface Props {
  order: IUberEatsIncomingOrder
  stage: UberEatsUrgencyStage | 'expired'
  timeLabel: string
  waitingCount: number
  isInFlight: boolean
  isAccepting: boolean
  acceptError?: string
  denyError?: string
  soundBlocked: boolean
  denyPicker?: React.ReactNode
  onAccept: () => void
  onDeny: () => void
  onViewOrder: () => void
  onDismiss: () => void
  onEnableSound: () => void
}

const { ACTIONS, BRAND } = THEME_COLORS

export const UberEatsIncomingOrderBarView = ({
  order,
  stage,
  timeLabel,
  waitingCount,
  isInFlight,
  isAccepting,
  acceptError,
  denyError,
  soundBlocked,
  denyPicker,
  onAccept,
  onDeny,
  onViewOrder,
  onDismiss,
  onEnableSound,
}: Props) => {
  const isExpired = stage === 'expired'
  const acceptTitle = isAccepting ? 'Accepting…' : acceptError ? 'Retry' : 'Accept'

  return (
    <section
      aria-label='Incoming Uber Eats order'
      data-testid='uber-eats-alert-bar'
      style={{
        flexShrink: 0,
        padding: '8px 16px',
        backgroundColor: UBER_EATS_TIME_PILL_STYLES[stage].bg,
        borderBottom: `1px solid ${BRAND.black}`,
        color: BRAND.black,
      }}
    >
      <div className='d-flex flex-wrap align-items-center' style={{ gap: 12 }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div role='status' style={{ fontWeight: 700 }}>
            {formatAlertBarHeadline(order)}
          </div>
          {acceptError && (
            <div role='alert' style={{ color: ACTIONS.dangerAlt, fontWeight: 600 }}>
              {`Not accepted — ${acceptError}. The order is still waiting.`}
            </div>
          )}
          {denyError && (
            <div role='alert' style={{ color: ACTIONS.dangerAlt, fontWeight: 600 }}>
              {`Deny did not go through — ${denyError}.`}
            </div>
          )}
        </div>

        <UberEatsStageBadge stage={stage} timeLabel={timeLabel} testId='uber-eats-alert-stage' />

        {waitingCount > 0 && (
          <span style={{ fontWeight: 600 }}>{`+${waitingCount} more waiting`}</span>
        )}

        {soundBlocked && (
          <UberEatsActionButton
            title='Sound off, click to enable'
            variant='link'
            onClick={onEnableSound}
          />
        )}

        <div className='d-flex' style={{ gap: 8 }}>
          {isExpired ? (
            <UberEatsActionButton title='Dismiss' onClick={onDismiss} />
          ) : (
            <>
              <UberEatsActionButton
                title={acceptTitle}
                variant='accept'
                disabled={isInFlight}
                onClick={onAccept}
              />
              {!denyPicker && (
                <UberEatsActionButton title='Deny' disabled={isInFlight} onClick={onDeny} />
              )}
              <UberEatsActionButton
                title='View Orders'
                variant='link'
                disabled={isInFlight}
                onClick={onViewOrder}
              />
            </>
          )}
        </div>
      </div>

      {denyPicker && !isExpired && <div className='mt-2'>{denyPicker}</div>}
    </section>
  )
}
